// Voice channel types — mirrored from the backend's Prisma enums, DTOs, and
// `voice-channel/gateway/voice-ws-event.types.ts`. Backend remains the
// source of truth; keep this file in lockstep when it changes.

import type { IAiCallHistoryRow } from '@/features/voice-agent/types';

// ── Enums ────────────────────────────────────────────────────────────────

export const CALL_DIRECTIONS = ['INBOUND', 'OUTBOUND'] as const;
export type CallDirection = (typeof CALL_DIRECTIONS)[number];

export const CALL_STATUSES = [
    'INITIATED',
    'RINGING',
    'IN_PROGRESS',
    'ON_HOLD',
    'TRANSFERRED',
    'COMPLETED',
    'FAILED',
    'BUSY',
    'NO_ANSWER',
    'CANCELED',
    'VOICEMAIL',
] as const;
// PARKED is deliberately outside CALL_STATUSES: the array feeds the history
// status filter, and calls never *end* PARKED — but the status is reachable
// live (busy-wait / hold queue) so the type and meta must know it.
export type CallStatus = (typeof CALL_STATUSES)[number] | 'PARKED';

/** Statuses where the call is actively connected on the widget. */
export const ACTIVE_CALL_STATUSES: readonly CallStatus[] = ['IN_PROGRESS', 'ON_HOLD'];
/** Statuses with no further transitions — matches the backend state machine. */
export const TERMINAL_CALL_STATUSES: readonly CallStatus[] = [
    'COMPLETED',
    'FAILED',
    'BUSY',
    'NO_ANSWER',
    'CANCELED',
    'VOICEMAIL',
];

export const CALL_DISPOSITIONS = ['INTERESTED', 'NOT_INTERESTED', 'FOLLOW_UP', 'OTHERS'] as const;
export type CallDisposition = (typeof CALL_DISPOSITIONS)[number];

export const VOICE_RECORDING_KINDS = ['CALL', 'VOICEMAIL'] as const;
export type VoiceRecordingKind = (typeof VOICE_RECORDING_KINDS)[number];

export const VOICE_RECORDING_STATUSES = ['PENDING', 'STORED', 'FAILED', 'DELETED'] as const;
export type VoiceRecordingStatus = (typeof VOICE_RECORDING_STATUSES)[number];

export const VOICE_RECORDING_POLICIES = [
    'ALWAYS',
    'NEVER',
    'INCOMING_ONLY',
    'OUTGOING_ONLY',
    'BUSINESS_HOURS_ONLY',
] as const;
export type VoiceRecordingPolicy = (typeof VOICE_RECORDING_POLICIES)[number];

export const VOICE_PRESENCE_STATUSES = ['ACTIVE', 'AWAY', 'BUSY', 'OFFLINE'] as const;
export type VoicePresenceStatus = (typeof VOICE_PRESENCE_STATUSES)[number];

// ── Shared shapes ────────────────────────────────────────────────────────

export interface IVoiceContact {
    readonly id: string;
    readonly firstName: string;
    readonly lastName: string | null;
    readonly phone: string;
}

/** Minimal user projection on call detail (agent / answered-by / transfer target). */
export interface IVoiceUserLite {
    readonly id: string;
    readonly firstName: string;
    readonly lastName: string;
}

export interface IVoiceChannelIdentity {
    readonly id: string;
    readonly identity: string;
    readonly displayName: string | null;
}

// ── Calls ────────────────────────────────────────────────────────────────

export interface ICall {
    readonly id: string;
    readonly direction: CallDirection;
    readonly status: CallStatus;
    readonly disposition: CallDisposition | null;
    readonly dispositionDescription: string | null;
    readonly notes: string | null;
    readonly fromNumber: string;
    readonly toNumber: string;
    readonly contactId: string | null;
    readonly contact: IVoiceContact | null;
    readonly userId: string | null;
    /** Owning agent, lite projection — for the table's Agent column. */
    readonly user: IVoiceUserLite | null;
    readonly channelIdentityId: string;
    readonly channelIdentity: IVoiceChannelIdentity;
    readonly ivrPath: string | null;
    readonly transferCount: number;
    readonly transferredToNumber: string | null;
    readonly recordingEnabled: boolean;
    readonly startedAt: string;
    readonly ringingAt: string | null;
    readonly answeredAt: string | null;
    readonly endedAt: string | null;
    readonly durationSeconds: number | null;
    readonly errorCode: string | null;
    readonly errorMessage: string | null;
    readonly createdAt: string;
    /** Present only on `includeRecordings` list queries (widget per-number timeline). */
    readonly recordings?: readonly IVoiceRecordingSummary[];
}

/** Who handled a Calls-history row (P9-8). */
export type CallHandledBy = 'TEAM' | 'AI';
/** The `handledBy` list filter, as it travels in the URL. */
export const HANDLED_BY_FILTERS = ['all', 'team', 'ai'] as const;
export type HandledByFilter = (typeof HANDLED_BY_FILTERS)[number];

/** A softphone row of the Calls history — the call, tagged. */
export type ITeamCallHistoryRow = ICall & { readonly handledBy: 'TEAM' };
/** One row of `GET /voice/calls` on the Calls page: a team call or an AI-answered call. */
export type ICallHistoryRow = ITeamCallHistoryRow | IAiCallHistoryRow;

export interface ICallDetail extends ICall {
    readonly recordings: readonly IVoiceRecording[];
    readonly user: IVoiceUserLite | null;
    readonly answeredByUser: IVoiceUserLite | null;
    readonly transferredToUser: IVoiceUserLite | null;
    /** Σ leg prices as a plain decimal string (e.g. "0.0234"); null until fetched. */
    readonly price: string | null;
    readonly priceUnit: string | null;
    readonly billableMinutes: number | null;
    /** ChannelProvider enum value, e.g. "TWILIO". */
    readonly provider: string;
    readonly dispositionSetAt: string | null;
    readonly dispositionSetBy: IVoiceUserLite | null;
    /** Inbox message this call is linked to, if any. */
    readonly messageId: string | null;
}

export interface IListCallsQuery {
    /** `all` (default) · `team` · `ai` — who handled the call. */
    readonly handledBy?: HandledByFilter;
    readonly direction?: CallDirection;
    readonly status?: CallStatus;
    readonly disposition?: CallDisposition;
    readonly userId?: string;
    /** Only the calls one AI employee answered (Phase 10, D11); the server then answers AI rows only. */
    readonly employeeId?: string;
    readonly contactId?: string;
    readonly channelIdentityId?: string;
    readonly hasRecording?: boolean;
    /** Exact E.164 of the other party — the widget's per-number timeline. */
    readonly counterpartNumber?: string;
    /** Part of the number the call came from — digits only, matched anywhere. */
    readonly fromNumber?: string;
    /** Attach a lite recordings projection to each row (opt-in). */
    readonly includeRecordings?: boolean;
    readonly dateFrom?: string;
    readonly dateTo?: string;
    readonly search?: string;
    readonly sortDir?: 'asc' | 'desc';
    readonly page?: number;
    readonly limit?: number;
}

export interface ICallAnalyticsQuery {
    readonly dateFrom?: string;
    readonly dateTo?: string;
}

export interface ICallAnalyticsAgentRow {
    readonly userId: string;
    readonly totalCalls: number;
    readonly totalMinutes: number;
}

export interface ICallAnalyticsDayRow {
    readonly date: string;
    readonly totalCalls: number;
    readonly inbound: number;
    readonly outbound: number;
    readonly missed: number;
    /** Mean talk time (minutes) over the day's measured-duration calls; 0 when none had a duration. */
    readonly avgMinutes: number;
    /** Total talk time (minutes) of the day's calls; absent from an older server. */
    readonly talkMinutes?: number;
}

export interface ICallAnalytics {
    readonly totalCalls: number;
    readonly byDirection: Record<string, number>;
    readonly byStatus: Record<string, number>;
    readonly byDisposition: Record<string, number>;
    readonly totalMinutes: number;
    readonly avgMinutes: number;
    readonly missedCalls: number;
    readonly missedRate: number;
    readonly perAgent: readonly ICallAnalyticsAgentRow[];
    readonly perDay: readonly ICallAnalyticsDayRow[];
    /** AI-answered calls the AI picked up (already inside the totals). */
    readonly answeredByAi: number;
}

export interface IInitiateCallInput {
    readonly contactId?: string;
    readonly toNumber?: string;
    readonly fromIdentityId?: string;
    /** Client-generated call id — lets the widget render the call before the
     *  round trip; the server creates the row under this exact id. */
    readonly callId?: string;
}

export interface IInitiateCallResult {
    readonly callId: string;
    readonly conferenceName: string;
    readonly toNumber: string;
    readonly fromNumber: string;
    readonly contactId: string | null;
    /**
     * SIP URI / DID the Telnyx browser leg dials to reach the conference-per-call
     * bridge (Telnyx WebRTC dials a destination directly — no TwiML-App
     * indirection). Undefined for Twilio. Confirmed by the Phase 0 spike.
     */
    readonly webrtcDestination?: string;
}

export interface ITransferCallInput {
    readonly targetUserId?: string;
    readonly targetNumber?: string;
}

export interface IUpdateCallDispositionInput {
    /** Optional — omit for a notes-only update (mirrors the backend DTO). */
    readonly disposition?: CallDisposition;
    readonly description?: string;
    readonly notes?: string;
}

// ── Recordings ───────────────────────────────────────────────────────────

/** Lite recording row attached to list calls when `includeRecordings` is set —
 *  playback fields only (no heard-by join, that stays detail-only). */
export interface IVoiceRecordingSummary {
    readonly id: string;
    readonly kind: VoiceRecordingKind;
    readonly status: VoiceRecordingStatus;
    readonly durationSeconds: number | null;
    readonly mimeType: string;
    readonly createdAt: string;
}

export interface IVoiceRecording {
    readonly id: string;
    readonly callId: string;
    readonly contactId: string | null;
    readonly channelIdentityId: string;
    readonly kind: VoiceRecordingKind;
    readonly status: VoiceRecordingStatus;
    readonly durationSeconds: number | null;
    readonly sizeBytes: number | null;
    readonly mimeType: string;
    readonly heardAt: string | null;
    /** Who listened first — present on call-detail recording rows; null when unheard. */
    readonly heardByUser: IVoiceUserLite | null;
    readonly createdAt: string;
}

export interface IListRecordingsQuery {
    readonly kind?: VoiceRecordingKind;
    readonly callId?: string;
    readonly contactId?: string;
    readonly heard?: boolean;
    readonly dateFrom?: string;
    readonly dateTo?: string;
    readonly page?: number;
    readonly limit?: number;
}

export interface IPresignedRecordingUrl {
    readonly url: string;
    readonly expiresAt: string;
}

// ── Blocked numbers (org-wide caller blocklist) ──────────────────────────

export interface IBlockedNumberCreator {
    readonly id: string;
    readonly firstName: string;
    readonly lastName: string;
}

export interface IBlockedNumber {
    readonly id: string;
    readonly phone: string;
    readonly reason: string | null;
    readonly sourceCallId: string | null;
    readonly createdBy: string | null;
    readonly createdByUser: IBlockedNumberCreator | null;
    readonly createdAt: string;
}

export interface IListBlockedNumbersQuery {
    readonly search?: string;
    readonly page?: number;
    readonly limit?: number;
}

export interface IBlockNumberInput {
    readonly phone: string;
    readonly reason?: string;
    readonly sourceCallId?: string;
}

/** Only the note is editable — the phone is the row's identity. */
export interface IUpdateBlockedNumberInput {
    readonly reason?: string;
}

// ── Presence ─────────────────────────────────────────────────────────────

/** Users without a stored row are absent from the snapshot = ACTIVE. */
export interface IVoicePresenceEntry {
    readonly userId: string;
    readonly status: VoicePresenceStatus;
    readonly connected: boolean;
}

// ── Token / device ───────────────────────────────────────────────────────

export interface IMintedVoiceToken {
    readonly token: string;
    readonly identity: string;
    readonly expiresAt: string;
    readonly canReceive: boolean;
    /**
     * Which browser SDK the token is for — Twilio access JWT vs Telnyx
     * telephony-credential login_token (both delivered in `token`). Absent →
     * 'twilio' (backward compatible with the current mint response).
     */
    readonly provider?: 'twilio' | 'telnyx';
}

// ── Settings (org-level) ────────────────────────────────────────────────

export interface IVoiceSettingsState {
    /** False while `needsReprovision` is set, even though `status` is ACTIVE —
     *  a stale-provider row cannot serve calls, so the page offers Enable. */
    readonly enabled: boolean;
    readonly status: string | null;
    /** Provider voice is provisioned against. */
    readonly provider: string | null;
    /** Provider on the SMS channel voice piggybacks; null when none connected. */
    readonly channelProvider: string | null;
    /** Voice is provisioned against a provider the org no longer uses — every
     *  call path fails until an admin re-enables. */
    readonly needsReprovision: boolean;
    readonly twimlAppSid: string | null;
    readonly apiKeySid: string | null;
    readonly defaultVoiceIdentityId: string | null;
    /** Org-wide fallback greeting — TTS or uploaded audio; null = system default. */
    readonly defaultVoicemailGreeting: IVoiceGreeting | null;
    /** Read-only fallback played when neither an org nor number greeting exists. */
    readonly systemVoicemailGreetingText: string;
    /** Org-wide "currently unavailable" announcement played before hanging up
     *  when voicemail is off — TTS or uploaded audio; null = system default. */
    readonly unavailableMessage: IVoiceGreeting | null;
    /** Read-only fallback announcement when the org configures none. */
    readonly systemUnavailableMessageText: string;
    readonly lastError: string | null;
}

// ── Per-number config ────────────────────────────────────────────────────

export interface IVoiceNumberSummary {
    readonly channelIdentityId: string;
    readonly identity: string;
    readonly displayName: string | null;
    readonly voiceCapable: boolean;
    readonly voiceEnabled: boolean;
    readonly recordingPolicy: VoiceRecordingPolicy;
    /** Handed to AI Phone Answering: the phone-line settings cannot use it. */
    readonly answeredByAi: boolean;
    /** The phone AI employee answering it, when one is named. */
    readonly assignedEmployee: IVoiceLineEmployee | null;
}

/** The phone AI employee a number is handed to. */
export interface IVoiceLineEmployee {
    readonly id: string;
    readonly name: string;
}

export interface IBusinessHoursInterval {
    readonly start: string; // "HH:mm"
    readonly end: string;
}

export type IBusinessHoursDay = readonly IBusinessHoursInterval[];

export interface IBusinessHoursWeekly {
    readonly mon?: IBusinessHoursDay;
    readonly tue?: IBusinessHoursDay;
    readonly wed?: IBusinessHoursDay;
    readonly thu?: IBusinessHoursDay;
    readonly fri?: IBusinessHoursDay;
    readonly sat?: IBusinessHoursDay;
    readonly sun?: IBusinessHoursDay;
}

export interface IBusinessHoursHoliday {
    readonly date: string; // "YYYY-MM-DD"
    readonly name: string;
}

export interface IBusinessHours {
    readonly timezone: string;
    readonly weekly: IBusinessHoursWeekly;
    readonly holidays?: readonly IBusinessHoursHoliday[];
}

export type VoiceGreetingMode = 'TTS' | 'AUDIO';

export interface IVoiceGreeting {
    readonly mode: VoiceGreetingMode;
    readonly text?: string;
    readonly storageKey?: string;
    /** Twilio TTS voice id (e.g. "Polly.Joanna"); unset = Twilio default. Only meaningful for mode TTS. */
    readonly voice?: string;
    /** Twilio language/locale code (e.g. "en-US"); unset = Twilio default. Only meaningful for mode TTS. */
    readonly language?: string;
}

export interface IGreetingUploadUrlInput {
    readonly fileName: string;
    readonly contentType: string;
    readonly sizeBytes: number;
}

export interface IGreetingUploadUrl {
    readonly uploadUrl: string;
    readonly storageKey: string;
}

export interface IVoiceRouteAction {
    readonly type: 'RING_USERS' | 'FORWARD_NUMBER' | 'VOICEMAIL';
    readonly userIds?: readonly string[];
    readonly number?: string;
    /** VOICEMAIL: greeting override; falls back to the number-level greeting. */
    readonly greeting?: IVoiceGreeting;
}

export interface IIvrMenuOption {
    readonly digit: string;
    readonly label: string;
    readonly action: IVoiceRouteAction;
}

export interface IIvrConfig {
    readonly greeting: IVoiceGreeting;
    readonly menu: readonly IIvrMenuOption[];
    readonly invalidRetries: number;
    readonly timeoutSec: number;
    /** How many times the whole menu may replay before the timeout action. */
    readonly menuRepeatLimit?: number;
    readonly timeoutAction: IVoiceRouteAction;
}

/** Whole-number inbound strategy — a number runs an IVR menu or forwarding. */
export type VoiceRoutingMode = 'IVR' | 'FORWARDING';

/**
 * A number's stored mode. `AI_AGENT` is set only by AI Phone Answering when an
 * AI employee takes the number; the phone-line settings never write it.
 */
export type VoiceNumberRoutingMode = VoiceRoutingMode | 'AI_AGENT';

/** After-hours behavior of an IVR-mode number. */
export type AfterHoursMode = 'IVR' | 'VOICEMAIL' | 'FORWARD_NUMBER';

/** The after-hours slot: run a menu (default), drop to voicemail, or forward out. */
export interface IIvrAfterHours {
    readonly mode: AfterHoursMode;
    readonly ivr?: IIvrConfig | null;
    readonly greeting?: IVoiceGreeting | null;
    readonly number?: string;
}

/**
 * Per-time-of-day IVR: a business-hours menu and a 3-way after-hours slot.
 * A null/absent slot means "no menu for that bucket".
 */
export interface IIvrSchedule {
    readonly businessHours?: IIvrConfig | null;
    readonly afterHours?: IIvrAfterHours | null;
}

export type IvrSlot = 'businessHours' | 'afterHours';

export interface IForwardingRule {
    readonly type: 'FORWARD_NUMBER' | 'VOICEMAIL' | 'RING_USERS';
    /** RING_USERS: teammates to ring. */
    readonly userIds?: readonly string[];
    readonly number?: string;
    /** VOICEMAIL: greeting override; falls back to the number-level greeting. */
    readonly greeting?: IVoiceGreeting;
}

/** One time-of-day forwarding slot: ring targets first, then per-scenario fallbacks. */
export interface IForwardingSlot {
    readonly ringTargets: readonly string[];
    readonly busy: IForwardingRule | null;
    readonly noAnswer: IForwardingRule | null;
    readonly unavailable: IForwardingRule | null;
}

export interface IForwarding {
    readonly businessHours: IForwardingSlot;
    readonly afterHours: IForwardingSlot;
}

export interface IVoiceNumberConfig {
    readonly channelIdentityId: string;
    readonly identity: string;
    readonly provider: 'TWILIO' | 'TELNYX';
    readonly voiceCapable: boolean;
    readonly voiceEnabled: boolean;
    readonly routingMode: VoiceNumberRoutingMode;
    /** The phone AI employee answering this number, when it is handed to one. */
    readonly assignedEmployee?: IVoiceLineEmployee | null;
    readonly recordingPolicy: VoiceRecordingPolicy;
    readonly businessHours: IBusinessHours | null;
    readonly ivrConfig: IIvrSchedule | null;
    readonly ringTimeoutSec: number;
    readonly maxWaitSeconds: number;
    readonly holdQueueEnabled: boolean;
    readonly maxHoldSeconds: number;
    readonly forwarding: IForwarding;
    readonly voicemailEnabled: boolean;
    readonly voicemailGreeting: IVoiceGreeting;
    readonly voicemailMaxLengthSec: number;
    /** What plays when this number has no greeting of its own (org/system fallback). */
    readonly effectiveDefaultVoicemailGreeting: IVoiceGreetingPreview;
    /** What the caller hears instead when voicemail is switched OFF. */
    readonly effectiveUnavailableMessage: IVoiceGreetingPreview;
}

/** Read-only quote of an org/system fallback, for previewing in the editor. */
export interface IVoiceGreetingPreview {
    readonly text: string;
    readonly source: 'ORG' | 'SYSTEM';
    /** True when the fallback is an uploaded file — `text` describes it
     *  rather than quoting it. */
    readonly isAudio?: boolean;
}

export interface IUpsertVoiceNumberConfigInput {
    readonly voiceEnabled: boolean;
    readonly routingMode: VoiceRoutingMode;
    readonly recordingPolicy: VoiceRecordingPolicy;
    readonly businessHours?: IBusinessHours | null;
    readonly ivrConfig?: IIvrSchedule | null;
    readonly ringTimeoutSec: number;
    readonly maxWaitSeconds?: number;
    readonly holdQueueEnabled?: boolean;
    readonly maxHoldSeconds?: number;
    readonly forwarding?: IForwarding;
    readonly voicemailEnabled: boolean;
    readonly voicemailGreeting?: IVoiceGreeting;
    readonly voicemailMaxLengthSec: number;
}

// ── WebSocket contract (mirrors backend voice-ws-event.types.ts) ────────

export const VOICE_WS_EVENT_NAMES = {
    CALL_INCOMING: 'voice:call:incoming',
    CALL_STATE: 'voice:call:state',
    CALL_ENDED: 'voice:call:ended',
    VOICEMAIL_NEW: 'voice:voicemail:new',
    RECORDING_READY: 'voice:recording:ready',
    PRESENCE_CHANGED: 'voice:presence:changed',
    CALL_PARKED: 'voice:call:parked',
    CALL_UNPARKED: 'voice:call:unparked',
} as const;

export type VoiceWsEventName = (typeof VOICE_WS_EVENT_NAMES)[keyof typeof VOICE_WS_EVENT_NAMES];

export interface IVoiceWsEnvelope<TPayload> {
    readonly eventId: string;
    readonly eventAt: string;
    readonly name: VoiceWsEventName;
    readonly payload: TPayload;
}

export interface IVoiceContactSnapshot {
    readonly id: string;
    readonly name: string;
}

export interface ICallIncomingPayload {
    readonly callId: string;
    readonly direction: CallDirection;
    readonly fromNumber: string;
    readonly toNumber: string;
    readonly contact: IVoiceContactSnapshot | null;
    readonly channelIdentity: IVoiceChannelIdentity;
}

/** A queued inbound call in the widget's finish-first ringing queue —
 *  the WS incoming payload plus when this client first saw it ring. */
export interface IRingingCall extends ICallIncomingPayload {
    readonly receivedAt: number;
}

export interface ICallParkedPayload extends ICallIncomingPayload {
    readonly parkedAt: string;
}

export interface ICallUnparkedPayload {
    readonly callId: string;
}

/** A parked caller shown in the widget's hold queue — same metadata as an
 *  incoming call plus when it was parked and when this client first saw it. */
export interface IHeldCall extends ICallIncomingPayload {
    readonly parkedAt: string;
    readonly heldSince: number;
}

export interface ICallStatePayload {
    readonly callId: string;
    readonly status: CallStatus;
    readonly direction: CallDirection;
    readonly contact: IVoiceContactSnapshot | null;
    readonly fromNumber: string;
    readonly toNumber: string;
    readonly answeredAt: string | null;
    readonly onHold: boolean;
    readonly recordingEnabled: boolean;
    readonly transferCount: number;
    readonly userId: string | null;
}

export interface ICallEndedPayload {
    readonly callId: string;
    readonly status: CallStatus;
    readonly durationSeconds: number | null;
    readonly endedAt: string | null;
    readonly userId: string | null;
    readonly contact: IVoiceContactSnapshot | null;
}

export interface IVoicemailNewPayload {
    readonly recordingId: string;
    readonly callId: string;
    readonly contactId: string | null;
    readonly channelIdentityId: string;
    readonly durationSeconds: number | null;
}

export interface IRecordingReadyPayload {
    readonly recordingId: string;
    readonly callId: string;
    readonly kind: 'CALL' | 'VOICEMAIL';
}

export interface IPresenceChangedPayload {
    readonly userId: string;
    readonly status: VoicePresenceStatus;
    readonly connected: boolean;
}
